import { useState, useEffect, useRef, useMemo } from "react";
import { SearchResults, Professional } from "@/hooks/useSearch";
import { LatLng } from "@/lib/geo";
import { ResultsList } from "./results";
import { GoogleMapView } from "./google-maps";
import  ArrowLeft  from "@/components/IconsTsx/ArrowLeft";
import { ArrowRight } from "@/components/IconsTsx/ArrowRight";
import styles from "./results-and-map-wrapper.module.css";
import PdfIcon from "@/components/IconsTsx/PdfIcon";
import { trackPdfDownloaded, trackPaginationClicked } from "@/lib/mixpanelClient";

interface ResultsAndMapWrapperProps {
  searchResults: SearchResults | undefined;
  selectedProfessional: Professional | null;
  onProfessionalSelect: (professional: Professional | null) => void;
  hasSearched?: boolean; // Nuevo prop para indicar si se ha realizado una búsqueda
  cartillaData?: { nombrePdf: string; url: string } | null; // Información de la cartilla disponible
  userCenter?: LatLng | null; // Centro de cercanía para mostrar en el mapa
  /** Repite la búsqueda a nivel país (mismos filtros, sin Pcia/Zona). */
  onShowAllCountry?: () => void;
  /** La zona elegida no tenía resultados y se amplió sola a nivel país. */
  fallbackActive?: boolean;
  /** La búsqueda actual es a nivel país (fallback automático O "Todos"
   *  manual) — no forzar el radio a 5km, taparía lo que se acaba de traer. */
  wideSearch?: boolean;
}

export function ResultsAndMapWrapper({
  searchResults,
  selectedProfessional,
  onProfessionalSelect,
  hasSearched = false,
  cartillaData = null,
  userCenter = null,
  onShowAllCountry,
  fallbackActive = false,
  wideSearch = false
}: ResultsAndMapWrapperProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [radiusKm, setRadiusKm] = useState<number | null>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  const isEmpty = !searchResults || searchResults.professionals.length === 0;

  // Hay "centro de cercanía" si algún prestador tiene distancia (real o estimada):
  // recién entonces tiene sentido ofrecer el filtro por radio.
  const hasCenter = useMemo(
    () =>
      !!searchResults?.professionals.some(
        (p) => typeof p.distanceKm === "number" || typeof p.distanceEstimatedKm === "number"
      ),
    [searchResults]
  );

  // Filtro por radio: deja los que están dentro de X km (por distancia real o
  // estimada). Los sin distancia (no ubicables) solo aparecen con "Todos".
  const displayProfessionals = useMemo(() => {
    const all = searchResults?.professionals ?? [];
    if (radiusKm == null) return all;
    return all.filter((p) => {
      const d =
        typeof p.distanceKm === "number"
          ? p.distanceKm
          : typeof p.distanceEstimatedKm === "number"
          ? p.distanceEstimatedKm
          : null;
      return d != null && d <= radiusKm;
    });
  }, [searchResults, radiusKm]);

  const radiusOptions = [5, 10, 25, 50];

  // Función para calcular en qué página está un profesional
  const getPageForProfessional = (professional: Professional): number => {
    if (!searchResults) return 1;

    const globalIndex = professional.globalIndex !== undefined
      ? professional.globalIndex
      : searchResults.professionals.findIndex(p => p.CODIGO === professional.CODIGO);

    if (globalIndex === -1) return 1;

    return Math.floor(globalIndex / searchResults.itemsPerPage) + 1;
  };

  // Estado para rastrear el profesional que debe ser scrolleado después del cambio de página
  const [professionalToScroll, setProfessionalToScroll] = useState<Professional | null>(null);

  // Función para manejar la selección de profesional con navegación automática
  const handleProfessionalSelectWithNavigation = (professional: Professional | null) => {
    if (professional && searchResults) {
      const targetPage = getPageForProfessional(professional);

      // Si el profesional está en una página diferente, navegar a esa página
      if (targetPage !== currentPage) {
        setCurrentPage(targetPage);
        setProfessionalToScroll(professional); // Guardar el profesional para scrollear después
      } else {
        // Si está en la misma página, hacer scroll inmediatamente
        setTimeout(() => {
          scrollToProfessional(professional);
        }, 50);
      }
    }

    // Llamar al callback original
    onProfessionalSelect(professional);
  };

  // Función para hacer scroll a un profesional específico
  const scrollToProfessional = (professional: Professional) => {
    if (resultsContainerRef.current) {
      const scrollContainer = resultsContainerRef.current.querySelector('[data-scroll-container]');
      if (scrollContainer) {
        // Buscar la card del profesional seleccionado
        const selectedCard = scrollContainer.querySelector(`[data-professional-id="${professional.CODIGO}-${professional.NOMBRE}-${professional.APELLIDO}"]`);
        if (selectedCard) {
          const cardRect = selectedCard.getBoundingClientRect();
          const containerRect = scrollContainer.getBoundingClientRect();
          const relativeTop = cardRect.top - containerRect.top + (scrollContainer as HTMLElement).scrollTop;

          (scrollContainer as HTMLElement).scrollTo({
            top: relativeTop,
            behavior: 'smooth'
          });
        }
      }
    }
  };

  // Efecto para hacer scroll al profesional después de que cambie la página
  useEffect(() => {
    if (professionalToScroll) {
      // Esperar a que el DOM se actualice después del cambio de página
      setTimeout(() => {
        scrollToProfessional(professionalToScroll);
        setProfessionalToScroll(null); // Limpiar el estado
      }, 150); // Delay más largo para asegurar que las cards estén renderizadas
    }
  }, [currentPage, professionalToScroll]);

  // Resetear a la primera página cuando cambian los resultados. Default del
  // radio: 5km (mostrar primero lo más cercano) si hay con qué filtrar. En
  // una búsqueda a nivel país (fallback automático O "Todos" manual) NO
  // arrancamos en 5km: son resultados que a propósito no están acotados a
  // una zona chica, y capar a 5km de entrada los taparía de nuevo (con
  // "Todos" además rompía el propio botón: el resultado nuevo volvía a
  // quedar en 5km apenas terminaba la búsqueda).
  useEffect(() => {
    setCurrentPage(1);
    setRadiusKm(hasCenter && !wideSearch ? 5 : null);
  }, [searchResults, hasCenter, wideSearch]);

  // Volver a la primera página al cambiar el radio
  useEffect(() => {
    setCurrentPage(1);
  }, [radiusKm]);

  const handlePageChange = (page: number) => {
    trackPaginationClicked({ paginaDestino: page }); // #14
    setCurrentPage(page);

    // Scroll suave a la primera card de la nueva página
    setTimeout(() => {
      if (resultsContainerRef.current) {
        // Buscar el contenedor de scroll real usando el atributo expuesto
        const scrollContainer = resultsContainerRef.current.querySelector('[data-scroll-container]');

        if (scrollContainer) {
          // Hacer scroll al inicio del contenedor de scroll
          (scrollContainer as HTMLElement).scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }
      }
    }, 100); // Pequeño delay para asegurar que el DOM se actualice
  };

  const handleCartillaClick = () => {
    if (cartillaData?.url) {
      trackPdfDownloaded({ resultCount: searchResults?.totalResults ?? 0 }); // #13
      // Abrir la URL en una nueva pestaña para descargar el archivo
      window.open(cartillaData.url, '_blank');
    }
  };

  // Paginación sobre la lista ya filtrada por radio. Se calcula siempre (con
  // searchResults undefined/vacío da listas vacías) para que el árbol de JSX
  // de abajo sea SIEMPRE el mismo, tenga o no resultados: GoogleMapView tiene
  // que quedar montado una sola vez y no desmontarse entre búsquedas, si no
  // sus efectos de reposicionar la cámara del mapa no llegan a aplicarse (el
  // marcador se ve bien porque es declarativo, pero la cámara se queda
  // donde estaba de la búsqueda anterior).
  const itemsPerPage = searchResults?.itemsPerPage ?? 16;
  const displayTotal = displayProfessionals.length;
  const displayTotalPages = Math.max(1, Math.ceil(displayTotal / itemsPerPage));
  const safePage = Math.min(currentPage, displayTotalPages);
  const startIndex = (safePage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentProfessionals = displayProfessionals.slice(startIndex, endIndex);

  const currentPageResults: SearchResults = {
    professionals: currentProfessionals.map((professional, localIndex) => ({
      ...professional,
      globalIndex: startIndex + localIndex // Agregar índice global
    })),
    totalResults: displayTotal,
    totalPages: displayTotalPages,
    currentPage: safePage,
    itemsPerPage,
  };

  return (
    <div className={styles.resultsAndMapWrapper}>
      {/* La zona elegida no tenía resultados directos: se amplió sola a
          nivel país (ver SearchBar.handleSearch). */}
      {hasSearched && !isEmpty && fallbackActive && (
        <div className={styles.fallbackNotice}>
          No encontramos resultados en la zona elegida. Te mostramos las opciones más cercanas del país.
        </div>
      )}

      {/* Información de resultados solo si se ha buscado */}
      {hasSearched && (
        <div className={styles.resultsInfo}>
          <span>
            {isEmpty
              ? "0 resultados"
              : radiusKm != null
              ? `${displayTotal} cerca (≤ ${radiusKm} km)`
              : `${displayTotal} resultados`}
          </span>

          {!isEmpty && hasCenter && (
            <div className={styles.radiusChips}>
              <span className={styles.radiusLabel}>Cerca:</span>
              {radiusOptions.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`${styles.radiusChip} ${radiusKm === r ? styles.radiusChipActive : ""}`}
                  onClick={() => setRadiusKm(r)}
                >
                  {r} km
                </button>
              ))}
              <button
                type="button"
                className={`${styles.radiusChip} ${radiusKm == null ? styles.radiusChipActive : ""}`}
                onClick={() => {
                  setRadiusKm(null);
                  // "Todos" acá no es solo sacar el tope de km: repite la
                  // búsqueda a nivel país (mismos filtros, sin Pcia/Zona) para
                  // no quedarse limitado a lo que ya había traído el backend
                  // para la ubicación elegida.
                  onShowAllCountry?.();
                }}
              >
                Todos
              </button>
            </div>
          )}

          <button
            className={styles.pdfButton}
            disabled={cartillaData === null}
            onClick={handleCartillaClick}
          >
            <PdfIcon />
            <span>Ver cartilla</span>
          </button>
        </div>
      )}

      {/* Contenedor del grid */}
      <div className={styles.gridContainer}>
        {/* Lista de resultados */}
        <div className={styles.resultsSection} ref={resultsContainerRef}>
          <ResultsList
            searchResults={isEmpty ? searchResults : currentPageResults}
            selectedProfessional={selectedProfessional}
            onProfessionalSelect={handleProfessionalSelectWithNavigation}
            hasSearched={hasSearched}
          />
        </div>

        {/* Mapa — SIEMPRE montado (ver comentario arriba de itemsPerPage) */}
        <div className={styles.mapSection}>
          <GoogleMapView
            professionals={isEmpty ? [] : displayProfessionals}
            selectedProfessional={isEmpty ? null : selectedProfessional}
            onProfessionalSelect={handleProfessionalSelectWithNavigation}
            userCenter={userCenter}
          />
        </div>
      </div>

      {/* Paginación (solo si hay más de una página) */}
      {!isEmpty && displayTotalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.paginationButton}
            onClick={() => handlePageChange(safePage - 1)}
            disabled={safePage === 1}
          >
            <ArrowLeft width={16} height={16}/>
          </button>

          <div className={styles.pageInfo}>
            {displayTotalPages === 1 ? (
              <span className={styles.currentPage}>1</span>
            ) : (
              <>
                <span className={styles.currentPage}>{safePage}</span>
                <span className={styles.pageSeparator}>-</span>
                <span className={styles.totalPages}>{displayTotalPages}</span>
              </>
            )}
          </div>

          <button
            className={styles.paginationButton}
            onClick={() => handlePageChange(safePage + 1)}
            disabled={safePage === displayTotalPages}
          >
            <ArrowRight width={16} height={16}/>
          </button>
        </div>
      )}
    </div>
  );
}
